import { useState } from 'react';
import { Box, Grid, MenuItem, Stack, TextField } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../../shared/components/PageHeader';
import { MonthFilterField, fromMonthValue, toMonthValue } from '../../../shared/components/MonthFilterField';
import { ErrorState } from '../../../shared/components/ErrorState';
import { describeApiError } from '../../../shared/api/errors';
import { KpiCard } from '../components/KpiCard';
import { PendenciasList, type PendenciaItem } from '../components/PendenciasList';
import { TrendChart } from '../components/TrendChart';
import { useDashboardKpis, useDashboardPending, useDashboardTrend } from '../hooks';

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function fmtBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  });
}

export function AdminDashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const now = new Date();
  const [ano, setAno] = useState(now.getFullYear());
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [meses, setMeses] = useState(12);

  const kpis = useDashboardKpis({ ano, mes });
  const pending = useDashboardPending();
  const trend = useDashboardTrend({ meses });

  const pendItems: PendenciaItem[] = [
    {
      key: 'nfsAValidar',
      label: t('features.dashboard.pendencias.admin.nfsAValidar'),
      count: pending.data?.nfsAValidar ?? 0,
      to: '/colaboradores/notas-fiscais',
      icon: <ReceiptLongIcon fontSize="small" color="warning" />,
      severity: 'warning',
    },
    {
      key: 'vencimentosPendentes',
      label: t('features.dashboard.pendencias.admin.vencimentos'),
      count: pending.data?.vencimentosPendentes ?? 0,
      to: '/clientes/requisicoes-compra',
      icon: <AssignmentLateIcon fontSize="small" color="error" />,
      severity: 'error',
    },
  ];

  const competenciaLabel = `${MESES[mes - 1]}/${ano}`;

  return (
    <Box>
      <PageHeader
        title={t('features.dashboard.title')}
        subtitle={t('features.dashboard.subtitle')}
        actions={
          <MonthFilterField
            label={t('components.monthFilter.competencia')}
            value={toMonthValue(ano, mes)}
            onChange={(value) => {
              const parsed = fromMonthValue(value);
              if (parsed.year == null || parsed.month == null) return;
              setAno(parsed.year);
              setMes(parsed.month);
            }}
          />
        }
      />

      {kpis.isError ? (
        <ErrorState description={describeApiError(kpis.error)} onRetry={() => kpis.refetch()} />
      ) : (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label={t('features.dashboard.kpis.faturamento')}
              caption={competenciaLabel}
              icon={<AttachMoneyIcon />}
              loading={kpis.isLoading}
              value={kpis.data ? fmtBRL(kpis.data.faturamento) : '—'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label={t('features.dashboard.kpis.horasLancadas')}
              caption={competenciaLabel}
              icon={<AccessTimeIcon />}
              loading={kpis.isLoading}
              value={kpis.data?.horasLancadas ?? '—'}
              to="/apontamentos"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label={t('features.dashboard.kpis.nfsPendentes')}
              icon={<ReceiptLongIcon />}
              loading={kpis.isLoading}
              value={kpis.data?.nfsPendentes ?? '—'}
              to="/colaboradores/notas-fiscais"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label={t('features.dashboard.kpis.colaboradoresAtivos')}
              icon={<GroupIcon />}
              loading={kpis.isLoading}
              value={kpis.data?.colaboradoresAtivos ?? '—'}
              to="/colaboradores"
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <TextField
              select
              size="small"
              label={t('features.dashboard.filters.periodo')}
              value={meses}
              onChange={(e) => setMeses(Number.parseInt(e.target.value, 10))}
              sx={{ minWidth: 160 }}
            >
              {[6, 12, 18, 24].map((n) => (
                <MenuItem key={n} value={n}>
                  {t('features.dashboard.filters.ultimosMeses', { count: n })}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TrendChart
            title={t('features.dashboard.trend.title')}
            data={trend.data?.pontos}
            loading={trend.isLoading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PendenciasList
            title={t('features.dashboard.pendencias.title')}
            items={pendItems}
            loading={pending.isLoading}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
